import { Button } from "./ui/button";
import { PageLink } from "./PageLink";
import { PageHero, HeroLink } from "./PageHero";
import { RunConsole, ChartAuditQueue, runs } from "./ProductVisuals";
import type { ReactNode } from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { ArrowRight, CheckCircle, UserCheck } from "lucide-react";
import { type IndustrySolution } from "../data/solutions";
import { Mark } from './Mark';

const industryVisual: Record<string, ReactNode> = {
  healthcare: <ChartAuditQueue />,
  bfsi: <RunConsole {...runs.loan} />,
  insurance: <RunConsole {...runs.claims} />,
  "hr-tech": <RunConsole {...runs.hr} />,
  "it-ops": <RunConsole {...runs.incident} />,
  legal: <RunConsole {...runs.legal} />,
  telecom: <RunConsole {...runs.telecom} />,
  manufacturing: <RunConsole {...runs.manufacturing} />,
  education: <RunConsole {...runs.admissions} />,
  "invoice-processing": <RunConsole {...runs.invoice} />,
};

interface IndustrySolutionPageProps {
  data: IndustrySolution;
  onNavigate: (page: string) => void;
  onNavigateToDemo: () => void;
}

export function IndustrySolutionPage({ data, onNavigate, onNavigateToDemo }: IndustrySolutionPageProps) {
  return (
    <div className="min-h-screen bg-white">
      <PageHero
        crumb={<><PageLink page={"solutions"} onNavigate={onNavigate} className="font-semibold text-royal-indigo hover:text-dark-indigo">Solutions</PageLink><span className="mx-1.5">/</span>{data.eyebrow}</>}
        title={data.headline}
        lede={data.subhead}
        actions={
          <>
            <Button variant="cta" size="cta" onClick={onNavigateToDemo}>Schedule a demo</Button>
            <HeroLink onClick={() => onNavigate("platform-domain-packs")}>About Domain Packs</HeroLink>
          </>
        }
        visual={industryVisual[data.slug]}
      />


      {/* Problem + Solve */}
      <section className="py-20 lg:py-24 bg-white border-b border-softblack-tint-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-3">The problem</p>
              <p className="text-[18px] text-soft-black leading-relaxed">{data.problem}</p>
            </div>
            <div>
              <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">{data.solveHeadline}</h2>
              <p className="text-[19px] lg:text-[20px] text-soft-black leading-relaxed">{data.solveBody}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Automate / Keep human */}
      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-6 items-start">
            <Card className="p-8 bg-white border-softblack-tint-20">
              <CardContent className="p-0">
                <h3 className="text-[22px] lg:text-[24px] font-semibold text-dark-indigo mb-5">What we automate</h3>
                <ul className="space-y-3">
                  {data.automate.map((a) => (
                    <li key={a} className="flex items-start gap-3 text-soft-black">
                      <CheckCircle className="w-5 h-5 text-royal-indigo flex-shrink-0 mt-0.5" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card className="p-8 bg-white border-indigo-tint-20">
              <CardContent className="p-0">
                <div className="flex items-center gap-3 mb-5">
                  <UserCheck className="w-6 h-6 text-royal-indigo" />
                  <h3 className="text-[22px] lg:text-[24px] font-semibold text-dark-indigo">What we always keep human</h3>
                </div>
                <p className="text-soft-black leading-relaxed">{data.keepHuman}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-3">
              {data.proofHeadline ?? "In production. Not in a pilot."}
            </p>
            <div className="grid sm:grid-cols-3 gap-6 mt-8">
              {data.proof.map((p) => (
                <Card key={p.label} className="p-8 border-softblack-tint-20">
                  <CardContent className="p-0">
                    <div className="font-display text-[40px] lg:text-[46px] leading-tight font-bold tracking-tight text-dark-indigo mb-2">{p.metric}</div>
                    <p className="text-sm text-soft-black">{p.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-sm text-soft-black mt-6 max-w-2xl mx-auto">{data.proofNote}</p>
          </div>
        </div>
      </section>

      {/* Solution Pack */}
      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-indigo-tint-20 text-royal-indigo mb-4">Domain Pack</Badge>
            <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">{data.pack.name}</h2>
            <p className="text-[22px] text-soft-black leading-relaxed mb-8">{data.pack.body}</p>
            <Button variant="outline" className="border-indigo-tint-50 text-soft-black hover:bg-softblack-tint-20" asChild><PageLink page={"platform-domain-packs"} onNavigate={onNavigate}>
              How Domain Packs work <ArrowRight className="w-4 h-4 ml-2" />
            </PageLink></Button>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-8 text-center">Related <Mark>capabilities</Mark></h2>
            <div className="grid md:grid-cols-2 gap-6">
              {data.related.map((r) => (
                <PageLink page={r.page} onNavigate={onNavigate}
                  key={r.page}
                  className="text-left p-6 rounded-xl border border-softblack-tint-20 hover:border-indigo-tint-50 transition-all"
                >
                  <h3 className="font-semibold text-dark-indigo mb-1 flex items-center justify-between">
                    {r.label}
                    <ArrowRight className="w-4 h-4 text-royal-indigo" />
                  </h3>
                  <p className="text-sm text-soft-black">{r.desc}</p>
                </PageLink>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-dark-indigo text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight mb-6">Move from AI experiment to <Mark>AI operation</Mark></h2>
          <p className="text-[24px] text-indigo-tint-20 mb-8 max-w-2xl mx-auto">
            Tell us the workflow that's burning your team's capacity. We'll show you how it runs on TurfAI.
          </p>
          <Button variant="onDark" size="lg" onClick={onNavigateToDemo}>
            Schedule a demo
          </Button>
        </div>
      </section>
    </div>
  );
}
